// UploadFile.js
import { useEffect, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import { buildCSV } from "../../utils/buildCSV";
import { showAppNotification } from "../../utils/notifications";
import { FILE_TYPES } from "./UploadFileTypes";

/* ---------- helpers ---------- */
const toNum = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n =
    typeof v === "number"
      ? v
      : parseFloat(String(v).replace(/[^0-9.,-]/g, "").replace(",", "."));
  return Number.isFinite(n) ? n : null;
};

const fmtTon = (v) => (v != null && Number.isFinite(v) ? `${v.toFixed(1)} t` : "-");
const fmtUnit = (v, u) => (v != null && Number.isFinite(v) ? `${v.toFixed(1)} ${u}` : "-");

/* ---------- sdx helpers ---------- */
const _sdxRaw = (e) => e?.detail?.value ?? e?.target?.value ?? e;

// Einzelwert (String) – für normale Selects
const readSdxValue = (e) => {
  const raw = _sdxRaw(e);
  if (Array.isArray(raw)) return readSdxValue(raw[0]);
  if (raw && typeof raw === 'object') return raw.value ?? raw.label ?? '';
  return String(raw ?? '');
};

/* =================================================================================
   Transformationsfunktionen: Daten → Go2Zero-kompatibles Objekt für buildCSV()
   ================================================================================= */

/**
 * Berechnet Emissionen (t) aus absoluten kWh-Werten eines Jahres.
 */
function computeEmissionsForYear({ stromKwh, gasKwh, oelKwh }) {
  const strom_t = stromKwh != null ? stromKwh : null;
  const heiz_t =
    (gasKwh != null ? gasKwh : 0) + (oelKwh != null ? oelKwh : 0);
  const heizung_t = (gasKwh != null || oelKwh != null) ? heiz_t : null;

  return {
    strom: strom_t != null ? { wert_tonnen: strom_t, scope: 2 } : null,
    heizung: heizung_t != null ? { wert_tonnen: heizung_t, scope: 1 } : null,
    fuhrpark: null, // derzeit nicht aus CarbonPulse abgeleitet
  };
}

/**
 * CarbonPulse → Go2Zero-kompatibles Datenobjekt (für EIN Jahr).
 * - Absolute Verbräuche (kWh/Jahr)
 * - Emissionen werden übernommen (berechnet)
 * - Felder, die später nützlich sein könnten, werden als leere Strings mitgegeben,
 *   damit buildCSV sie in der üblichen Struktur mitscheibt.
 */
function transformCarbonPulseToGo2ZeroFormat(allData, year, formData, formMissing) {
  const jw = allData?.jahreswerte || {};

  const strom = jw?.strom_kwh_total?.[year] ?? null;
  const gas = jw?.heizung_gas_kwh_total?.[year] ?? null;
  const gas_einheit = jw?.heizung_gas_einheit?.[year] ?? "";
  const oel_kwh = jw?.heizung_oel_kwh_total?.[year] ?? null;
  const oel = oel_kwh / 10; //from kwh to litre 10kwh = 1l
  const holz = jw?.heizung_holz_kg_total?.[year] ?? null;
  const fernwaerme_menge = jw?.heizung_fernwaerme_total?.[year] ?? null;
  const fernwaerme_einheit = jw?.heizung_fernwaerme_einheit?.[year] ?? "";
  const wärmepumpe = jw?.heizung_waermepumpe_total?.[year] ?? null;

  const yearRows = allData?.dataByYear?.[year] ?? {};

  // Fehlende Eingabefelder pro Jahr (aus Mehrjahres-Form)
  const abfallY =
    allData?.abfallByYear?.[year] ??
    (allData?.abfall?.menge_tonnen != null ? allData.abfall.menge_tonnen : null);

  const matsY =
    allData?.verbrauchsmaterialienByYear?.[year] ??
    allData?.verbrauchsmaterialien ??
    null;

  // Emissionen pro Jahr berechnen
  const emissionen = computeEmissionsForYear({
    stromKwh: strom,
    gasKwh: gas,
    oelKwh: oel,
  });

  // Heizung immer mit beiden Carriern ausgeben (mit leerem amount, wenn nicht vorhanden)
  const heizungArray = [
    { area: "", carrier: "Gas", amount: gas ?? "", unit: gas_einheit },
    { area: "", carrier: "Öl", amount: oel ?? "", unit: "l" },
    { area: "", carrier: "Holz", amount: holz ?? "", unit: "kg" },
    { area: "", carrier: "Fernwärme", amount: fernwaerme_menge ?? "", unit: fernwaerme_einheit },
    { area: "", carrier: "Wärmepumpe", amount: wärmepumpe ?? "", unit: "" },
  ];

  const heizungRows = yearRows.heizung ?? allData?.heizung ?? heizungArray;
  const treibstoffRows = yearRows.treibstoff ?? allData?.treibstoff ?? [];
  const fossileFahrzeuge = yearRows.fossileFahrzeuge ?? allData?.fossileFahrzeuge ?? [];
  const beleuchtungRows = yearRows.beleuchtung ?? allData?.beleuchtung ?? [];
  const lackierkabinenRows = yearRows.lackierkabinen ?? allData?.lackierkabinen ?? [];

  // Objekt im buildCSV-Format (long format) – inkl. leerer Felder
  return {
    // ---------- Allgemein ----------
    referenzjahr: year,
    mitarbeitende: formMissing?.mitarbeitende ?? formData?.mitarbeitende ?? "",
    bereiche: formMissing?.bereiche ?? formData?.bereiche ?? [],
    lackierkabinen: lackierkabinenRows,
    produktiveMitarbeitende: formMissing?.produktiveMitarbeitende ?? formData?.produktiveMitarbeitende ?? "",
    reparaturen: formMissing?.reparaturen ?? formData?.reparaturen ?? "",
    stunden: formMissing?.stunden ?? formData?.stunden ?? "",



    // ---------- Strom ----------
    strommix: formMissing?.strommix ?? formData?.strommix ?? "", // Aus formMissing oder formData
    renewableShare: formMissing?.renewableShare ?? formData?.renewableShare ?? "", // Aus formMissing oder formData
    mengeStrom: strom ?? "", // ABSOLUT kWh/Jahr (leer, wenn unbekannt)
    pvOwn: formMissing?.pvOwn ?? formData?.pvOwn ?? "", // Aus formMissing oder formData
    pvMengeKwh: formMissing?.pvMengeKwh ?? formData?.pvMengeKwh ?? "", // Aus formMissing oder formData
    wärmepumpe: formMissing?.wärmepumpe ?? formData?.wärmepumpe ?? "",
    wärmepumpeBereiche: formMissing?.wärmepumpeBereiche ?? formData?.wärmepumpeBereiche ?? "",

    // ---------- Heizung ----------
    heizung: heizungRows,

    // ---------- Treibstoff ----------
    treibstoff: treibstoffRows,

    // ----------- Fossile Fahrzeuge ------------
    fossileFahrzeuge: fossileFahrzeuge,

    // ---------- Beleuchtung ----------
    beleuchtung: beleuchtungRows,

    // ---------- Emissionen ----------
    gesamt_co2: "", // Gesamt kann optional leer bleiben
    spezifisch_co2: "", // spezifisch (kg/m²) hier bewusst leer
    emissionen,

    // ---------- Einkauf / Abfall ----------
    verbrauchsmaterialLack: "", // legacy keys (werden vom CSV-Builder auch akzeptiert)
    verbrauchsmaterialAllg: "",
    verbrauchsmaterialErsatzteile: "",
    abfallTonnen: "",

    // ----------- Beleuchtung ------------
    beleuchtung: allData?.beleuchtung ?? [],

    // ----------- Lack + Betriebsgrösse ------------
    lackmix: formMissing?.lackmix ?? formData?.lackmix ?? [],
    lufttrocknendShare: formMissing?.lufttrocknendShare ?? formData?.lufttrocknendShare ?? "",
    betriebsgrösse: formData?.betriebsgrösse ?? "",

    // neue, strukturierte Container (werden vom CSV-Builder ebenfalls erkannt)
    verbrauchsmaterialien: matsY
      ? {
        lack_chf: matsY.lack_chf ?? "",
        materialien_chf: matsY.materialien_chf ?? "",
        ersatzteile_chf: matsY.ersatzteile_chf ?? "",
      }
      : {
        lack_chf: "",
        materialien_chf: "",
        ersatzteile_chf: "",
      },
    abfall:
      abfallY != null
        ? { menge_tonnen: abfallY }
        : { menge_tonnen: "" },
  };
}

/**
 * Go2Zero → Go2Zero-kompatibles Datenobjekt (vereinheitlicht, mit leeren Feldern).
 * Falls Go2Zero nicht alle Felder liefert, werden leere Platzhalter gesetzt,
 * damit buildCSV eine konsistente CSV-Struktur erzeugt.
 */
function transformGo2ZeroToUnified(data, formData, formMissing) {
  // Heizung in Go2Zero.parse() liegt ggf. nicht als Array vor → mit Platzhaltern erzeugen

  return {
    // ---------- Allgemein ----------
    referenzjahr: data?.referenzjahr ?? "",
    mitarbeitende: formMissing?.mitarbeitende ?? formData?.mitarbeitende ?? "",
    bereiche: formMissing?.bereiche ?? formData?.bereiche ?? [],
    lackierkabinen: data?.lackierkabinen ?? [],
    produktiveMitarbeitende: formMissing?.produktiveMitarbeitende ?? formData?.produktiveMitarbeitende ?? "",
    reparaturen: formMissing?.reparaturen ?? formData?.reparaturen ?? "",
    stunden: formMissing?.stunden ?? formData?.stunden ?? "",

    // ---------- Strom ----------
    strommix: formMissing?.strommix ?? formData?.strommix ?? "",
    renewableShare: formMissing?.renewableShare ?? formData?.renewableShare ?? "",
    mengeStrom: "", // Go2Zero liefert hier idR nicht den absoluten Wert → leer lassen
    pvOwn: formMissing?.pvOwn ?? formData?.pvOwn ?? "",
    pvMengeKwh: formMissing?.pvMengeKwh ?? formData?.pvMengeKwh ?? "",
    wärmepumpe: formData?.wärmepumpe ?? "", // Aus formData
    wärmepumpeBereiche: formData?.wärmepumpeBereiche ?? "", // Aus formData

    // ---------- Heizung ----------
    heizung: data?.heizung ?? [],

    // ---------- Treibstoff ----------
    treibstoff: data?.treibstoff ?? [],
    fossileFahrzeuge: data?.fossileFahrzeuge ?? null,
    // ---------- Emissionen ----------
    gesamt_co2: data?.gesamt_co2 ?? "",
    spezifisch_co2: data?.spezifisch_co2 ?? "",
    emissionen: data?.emissionen ?? null,

    // ---------- Einkauf / Abfall ----------
    verbrauchsmaterialLack: "",
    verbrauchsmaterialAllg: "",
    verbrauchsmaterialErsatzteile: "",
    abfallTonnen: "",

    verbrauchsmaterialien: data?.verbrauchsmaterialien ?? {
      lack_chf: "",
      materialien_chf: "",
      ersatzteile_chf: "",
    },
    abfall: data?.abfall ?? { menge_tonnen: "" },

    // ----------- Beleuchtung --------------
    beleuchtung: data?.beleuchtung ?? [],

    // ----------- Lackmix + Betriebsgrösse --------------
    lackmix: formMissing?.lackmix ?? formData?.lackmix ?? [],
    lufttrocknendShare: formMissing?.lufttrocknendShare ?? formData?.lufttrocknendShare ?? "",
    betriebsgrösse: formData?.betriebsgrösse ?? "",
  };
}

/* =================================================================================
   React-Komponente
   ================================================================================= */

export default function UploadFile({ user, onBack, onSaved }) {
  const [step, setStep] = useState("upload");
  const [data, setData] = useState(null);
  const [baseData, setBaseData] = useState(null);
  const [fileType, setFileType] = useState(null);
  const [fileName, setFileName] = useState("");
  const [error, setError] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const [selectedYear, setSelectedYear] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const inputRef = useRef(null);

  const company = user?.attributes?.["custom:company"];
  const userId = user?.username;

  // Ein-Jahres-Form (Go2Zero oder fallback)
  const [formMissing, setFormMissing] = useState({
    abfallTonnen: "",
    verbrauchsmaterialLack: "",
    verbrauchsmaterialAllg: "",
    verbrauchsmaterialErsatzteile: "",
  });
  // Mehrjahres-Form (CarbonPulse)
  const [formMissingByYear, setFormMissingByYear] = useState({});

  // Form data for wärmepumpe etc.
  const [formData, setFormData] = useState({});

  const handleChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  // ByYear Row States für CarbonPulse
  const [heizungByYear, setHeizungByYear] = useState({});
  const [treibstoffByYear, setTreibstoffByYear] = useState({});
  const [fossilByYear, setFossilByYear] = useState({});
  const [lightByYear, setLightByYear] = useState({});
  const [lackierkabinenByYear, setLackierkabinenByYear] = useState({});

  // Shared Row States für Go2Zero
  const [heizungRows, setHeizungRows] = useState([{ id: 1, area: "", carrier: "", unit: "", amount: "" }]);
  const [heizungRowId, setHeizungRowId] = useState(2);
  const [lackierkabinenRows, setLackierkabinenRows] = useState([{ id: 1, area: "" }]);
  const [lackierkabinenRowId, setLackierkabinenRowId] = useState(2);
  const [fossilRows, setFossilRows] = useState([{ id: 1, type: "", amount: "" }]);
  const [fossilRowId, setFossilRowId] = useState(2);
  const [lightRows, setLightRows] = useState([{ id: 1, type: "", amount: "" }]);
  const [lightRowId, setLightRowId] = useState(2);
  const [treibstoffRows, setTreibstoffRows] = useState([{ id: 1, type: "", unit: "", amount: "" }]);
  const [treibstoffRowId, setTreibstoffRowId] = useState(2);

  // Helpers für byYear Rows - gibt aktuelle Rows für Jahr y zurück
  const getHeizungRows = (y) => heizungByYear[y] ?? [{ id: 1, area: "", carrier: "", unit: "", amount: "" }];
  const getTreibstoffRows = (y) => treibstoffByYear[y] ?? [{ id: 1, type: "", unit: "", amount: "" }];
  const getFossilRows = (y) => fossilByYear[y] ?? [{ id: 1, type: "", amount: "" }];
  const getLightRows = (y) => lightByYear[y] ?? [{ id: 1, type: "", amount: "" }];
  const getLackierkabinenRows = (y) => lackierkabinenByYear[y] ?? [{ id: 1, area: "" }];

  // Update Helpers für byYear
  const updateHeizungByYear = (y, updater) => setHeizungByYear(prev => ({ ...prev, [y]: typeof updater === "function" ? updater(prev[y] ?? [{ id: 1, area: "", carrier: "", unit: "", amount: "" }]) : updater }));
  const updateTreibstoffByYear = (y, updater) => setTreibstoffByYear(prev => ({ ...prev, [y]: typeof updater === "function" ? updater(prev[y] ?? [{ id: 1, type: "", unit: "", amount: "" }]) : updater }));
  const updateFossilByYear = (y, updater) => setFossilByYear(prev => ({ ...prev, [y]: typeof updater === "function" ? updater(prev[y] ?? [{ id: 1, type: "", amount: "" }]) : updater }));
  const updateLightByYear = (y, updater) => setLightByYear(prev => ({ ...prev, [y]: typeof updater === "function" ? updater(prev[y] ?? [{ id: 1, type: "", amount: "" }]) : updater }));
  const updateLackierkabinenByYear = (y, updater) => setLackierkabinenByYear(prev => ({ ...prev, [y]: typeof updater === "function" ? updater(prev[y] ?? [{ id: 1, area: "" }]) : updater }));

  // Go2Zero Row Handlers (unverändert)
  const addHeizungRow = () => { setHeizungRows(prev => [...prev, { id: heizungRowId, area: "", carrier: "", unit: "", amount: "" }]); setHeizungRowId(n => n + 1); };
  const updateHeizungRow = (id, patch) => setHeizungRows(prev => prev.map(r => r.id === id ? { ...r, ...patch } : r));
  const removeHeizungRow = (id) => setHeizungRows(prev => prev.filter(r => r.id !== id));
  const addLackierkabinenRow = () => { setLackierkabinenRows(prev => [...prev, { id: lackierkabinenRowId, area: "" }]); setLackierkabinenRowId(n => n + 1); };
  const updateLackierkabinenRow = (id, patch) => setLackierkabinenRows(prev => prev.map(r => r.id === id ? { ...r, ...patch } : r));
  const removeLackierkabinenRow = (id) => setLackierkabinenRows(prev => prev.filter(r => r.id !== id));
  const addFossilRow = () => { setFossilRows(prev => [...prev, { id: fossilRowId, type: "", amount: "" }]); setFossilRowId(n => n + 1); };
  const updateFossilRow = (id, patch) => setFossilRows(prev => prev.map(r => r.id === id ? { ...r, ...patch } : r));
  const removeFossilRow = (id) => setFossilRows(prev => prev.filter(r => r.id !== id));
  const addLightRow = () => { setLightRows(prev => [...prev, { id: lightRowId, type: "", amount: "" }]); setLightRowId(n => n + 1); };
  const updateLightRow = (id, patch) => setLightRows(prev => prev.map(r => r.id === id ? { ...r, ...patch } : r));
  const removeLightRow = (id) => setLightRows(prev => prev.filter(r => r.id !== id));
  const addTreibstoffRow = () => { setTreibstoffRows(prev => [...prev, { id: treibstoffRowId, type: "", unit: "", amount: "" }]); setTreibstoffRowId(n => n + 1); };
  const updateTreibstoffRow = (id, patch) => setTreibstoffRows(prev => prev.map(r => r.id === id ? { ...r, ...patch } : r));
  const removeTreibstoffRow = (id) => setTreibstoffRows(prev => prev.filter(r => r.id !== id));

  // Scroll to top when entering form step
  useEffect(() => {
    if (step === "form") {
      window.scrollTo(0, 0);
    }
  }, [step]);

  const ACCEPT = ".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

  async function parseFile(file) {
    setError("");
    setFileName(file.name);
    try {
      const arrayBuffer = await file.arrayBuffer();
      const wb = XLSX.read(arrayBuffer, { type: "array" });

      const handler = FILE_TYPES.find((t) => t.detect(wb, file));
      if (!handler) {
        setError("Unbekannter Dateityp. Unterstützt: Go2Zero, CarbonPulse.");
        setBaseData(null);
        setData(null);
        setStep("upload");
        return;
      }

      const parsed = handler.parse(wb);
      setBaseData(parsed);
      setFileType(handler);
      setFormMissing({ ...handler.missingDefaults });

      // Mehrjahres-Form vorbereiten (nur CarbonPulse)
      if (handler.type === "CarbonPulse") {
        const years = parsed?.availableYears || [];
        const init = {};
        for (const y of years) {
          init[y] = {
            abfallTonnen: "",
            verbrauchsmaterialLack: "",
            verbrauchsmaterialAllg: "",
            verbrauchsmaterialErsatzteile: "",
          };
        }
        setFormMissingByYear(init);
      } else {
        setFormMissingByYear({});
      }

      setSelectedYear(parsed?.referenzjahr ?? null);
      setStep("form");
    } catch (err) {
      console.error(err);
      setError("Fehler beim Verarbeiten der Datei. Bitte eine gültige .xlsx laden.");
      setBaseData(null);
      setData(null);
      setStep("upload");
    }
  }

  function handleInputChange(e) {
    const file = e.target.files?.[0];
    if (file) parseFile(file);
    e.target.value = "";
  }

  function onDragOver(e) {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }
  function onDragLeave(e) {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }
  function onDrop(e) {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    if (!ACCEPT.split(",").some((t) => file.type === t || file.name.toLowerCase().endsWith(".xlsx"))) {
      setError("Bitte eine .xlsx-Datei hochladen.");
      return;
    }
    parseFile(file);
  }

  // Speichern/mergen: manuelle Felder in baseData übernehmen, dann Summary
  function handleSaveForm() {
    if (!baseData || !fileType) return;
    const merged =
      fileType.type === "CarbonPulse"
        ? fileType.mergeMissing(baseData, formMissingByYear, toNum)
        : fileType.mergeMissing(baseData, formMissing, toNum);

    if (fileType.type === "CarbonPulse") {
      // Alle byYear Daten zusammenbauen
      const dataByYear = {};
      for (const yr of (baseData?.availableYears ?? [])) {
        dataByYear[yr] = {
          heizung: getHeizungRows(yr),
          treibstoff: getTreibstoffRows(yr),
          fossileFahrzeuge: getFossilRows(yr),
          beleuchtung: getLightRows(yr),
          lackierkabinen: getLackierkabinenRows(yr),
        };
      }
      setData({
        ...merged,
        dataByYear,
        formMissingByYear,
        ...formMissing,
      });
    } else {
      setData({
        ...merged,
        heizung: heizungRows,
        lackierkabinen: lackierkabinenRows,
        fossileFahrzeuge: fossilRows,
        beleuchtung: lightRows,
        treibstoff: treibstoffRows,
        ...formMissing,
      });
    }
    setCurrentStep(0);
    setStep("summary");
  }

  // Anzeige-Modell (für CarbonPulse pro Jahr Emissionen/Verbrauch berechnen)
  const display = useMemo(() => {
    if (!data) return null;

    if (fileType?.type !== "CarbonPulse") return {
      ...data,
      wärmepumpe: formData.wärmepumpe,
      wärmepumpeBereiche: formData.wärmepumpeBereiche,

    };

    const y = selectedYear ?? data.referenzjahr;
    const jw = data.jahreswerte || {};
    const strom = jw.strom_kwh_total?.[y] ?? null;
    const gas = jw.heizung_gas_kwh_total?.[y] ?? 0;
    const oel = jw.heizung_oel_kwh_total?.[y] ?? 0;
    const abfallY = data.abfallByYear?.[y] ?? data.abfall?.menge_tonnen ?? null;
    const matsY = data.verbrauchsmaterialienByYear?.[y] ?? data.verbrauchsmaterialien ?? null;

    // Jahresspezifische Rows und formMissing holen
    const yearData = data.dataByYear?.[y] ?? {};
    const yearForm = data.formMissingByYear?.[y] ?? {};

    return {
      ...data,
      referenzjahr: y,
      // Jahresspezifische Felder aus formMissingByYear
      bereiche: yearForm.bereiche ?? data.bereiche ?? [],
      mitarbeitende: yearForm.mitarbeitende ?? data.mitarbeitende,
      produktiveMitarbeitende: yearForm.produktiveMitarbeitende ?? data.produktiveMitarbeitende,
      reparaturen: yearForm.reparaturen ?? data.reparaturen,
      stunden: yearForm.stunden ?? data.stunden,
      strommix: yearForm.strommix ?? data.strommix,
      pvOwn: yearForm.pvOwn ?? data.pvOwn,
      pvMengeKwh: yearForm.pvMengeKwh ?? data.pvMengeKwh,
      renewableShare: yearForm.renewableShare ?? data.renewableShare,
      betriebsgrösse: yearForm.betriebsgrösse ?? data.betriebsgrösse,
      lackmix: yearForm.lackmix ?? data.lackmix ?? "",
      lufttrocknendShare: yearForm.lufttrocknendShare ?? data.lufttrocknendShare,
      wärmepumpe: yearForm.wärmepumpe ?? formData.wärmepumpe,
      wärmepumpeBereiche: yearForm.wärmepumpeBereiche ?? formData.wärmepumpeBereiche,

      // Jahresspezifische Rows
      heizung: yearData.heizung ?? [],
      treibstoff: yearData.treibstoff ?? [],
      fossileFahrzeuge: yearData.fossileFahrzeuge ?? [],
      beleuchtung: yearData.beleuchtung ?? [],
      lackierkabinen: yearData.lackierkabinen ?? [],
      emissionen: computeEmissionsForYear({ stromKwh: strom ?? null, gasKwh: gas || null, oelKwh: oel || null }),
      verbrauch: { ...data.verbrauch, strom_kwh_m2: strom ?? null, heizung_kwh_m2: (gas + oel) || null },
      abfall: { menge_tonnen: abfallY ?? null },
      verbrauchsmaterialien: matsY || { lack_chf: null, materialien_chf: null, ersatzteile_chf: null },
      lackmix: data.lackmix ?? "",
      lufttrocknendShare: data.lufttrocknendShare,
      wärmepumpe: formData.wärmepumpe,
      wärmepumpeBereiche: formData.wärmepumpeBereiche,
    };
  }, [data, fileType, selectedYear, formData]);
  const summarySectionStyle = {
    padding: 16,
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 12,
  };
  /* =================================================================================
    CSV-Upload: pro Jahr eine Datei, im Go2Zero-kompatiblen CSV-Format.
     ================================================================================= */
  async function uploadCSVToS3() {
    try {
      const payload = display ?? data ?? baseData;
      if (!payload || !fileType) return;

      // CarbonPulse → mehrere Jahre, Go2Zero → meist 1 Jahr
      const years =
        fileType.type === "CarbonPulse"
          ? payload.availableYears ?? (data?.availableYears ?? [payload.referenzjahr ?? new Date().getFullYear()])
          : [payload.referenzjahr ?? new Date().getFullYear()];

      for (const year of years) {
        let finalData;

        if (fileType.type === "CarbonPulse") {
          // CarbonPulse: Jahrweises Transformieren mit Jahresformular-Daten
          finalData = transformCarbonPulseToGo2ZeroFormat(payload, year, formData, formMissingByYear?.[year] ?? {});
        } else {
          // Go2Zero: bereits im Ziel-Format – aber wir setzen leere Platzhalterfelder
          finalData = transformGo2ZeroToUnified(payload, formData, formMissing);
        }

        const csv = buildCSV({ userId, company, data: finalData });
        const BOM = "\uFEFF";
        const blob = new Blob([BOM, csv], { type: "text/csv;charset=utf-8;" });

        const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
        const filename = `${fileType.type}-${year}-${company ?? "unbekannt"}_${timestamp}.csv`;

        const res = await fetch(
          `https://3h7d6zdnlo6xp4l2shhw4zx6540eaeoo.lambda-url.eu-west-1.on.aws/?filename=${encodeURIComponent(
            filename
          )}&folder=${fileType.type.toLowerCase()}`
        );
        if (!res.ok) {
          throw new Error(`Signierte Upload-URL konnte nicht geladen werden (${res.status})`);
        }

        const { uploadUrl } = await res.json();
        if (!uploadUrl) {
          throw new Error("Upload-URL fehlt in der Serverantwort");
        }

        const uploadRes = await fetch(uploadUrl, {
          method: "PUT",
          body: blob,
          headers: { "Content-Type": "text/csv" },
        });
        if (!uploadRes.ok) {
          throw new Error(`CSV-Upload fehlgeschlagen (${uploadRes.status})`);
        }

      }

      showAppNotification({
        message: `${fileType.type}: ${years.length} Datei(en) erfolgreich hochgeladen!`,
        type: "confirmation",
      });
      if (typeof onSaved === "function") onSaved();
    } catch (error) {
      console.error("uploadCSVToS3 failed:", error);
      const message =
        error instanceof Error ? error.message : "Unbekannter Fehler beim Speichern";
      showAppNotification({
        message: `Speichern fehlgeschlagen: ${message}`,
        type: "error",
      });
    }
  }

  return (
    <div className="sdx" style={{ maxWidth: 1100, margin: "0 auto", padding: "2rem" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24 }}>
        <sdx-button onClick={onBack} theme="secondary" label="zurück"></sdx-button>
        <h1 style={{ margin: 0 }}>
          {step === "upload" && "Excel hochladen (Go2Zero, CarbonPulse)"}
          {step === "form" && "Details ergänzen"}
          {step === "summary" && "Auswertung"}
        </h1>
      </div>

      {/* STEP 1: Upload */}
      {step === "upload" && (
        <>
          <div style={{ position: "relative", marginBottom: 16 }}>
            <div
              className={`empty-state ${isDragging ? "is-dragging" : ""}`}
              style={{
                width: "100%",
                border: "1px dashed #cbd5e1",
                borderRadius: 12,
                overflow: "hidden",
              }}
              onDragEnter={() => setIsDragging(true)}
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
              onDrop={onDrop}
            >
              <form onSubmit={(e) => e.preventDefault()}>
                <div style={{ padding: 24, background: "#fff", textAlign: "center" }}>
                  <div style={{ fontWeight: 600 }}>Noch keine Datei hochgeladen</div>
                  <div style={{ color: "#64748b" }}>
                    Ziehe eine <b>.xlsx</b>-Datei hierher oder klicke auf „Upload“
                  </div>
                  <div style={{ marginTop: 12 }}>
                    <input
                      ref={inputRef}
                      type="file"
                      accept={ACCEPT}
                      onChange={handleInputChange}
                      style={{ display: "none" }}
                    />
                    <sdx-button
                      label="Upload"
                      onClick={() => inputRef.current?.click()}
                    ></sdx-button>
                  </div>
                  {fileName && (
                    <div style={{ marginTop: 8, color: "#64748b" }}>Gewählt: {fileName}</div>
                  )}
                </div>
              </form>
            </div>
          </div>

          {error && (
            <div
              style={{
                background: "#fee2e2",
                border: "1px solid #fecaca",
                color: "#991b1b",
                borderRadius: 8,
                padding: 12,
                marginBottom: 16,
              }}
            >
              {error}
            </div>
          )}
        </>
      )}

      {/* STEP 2: Form */}
      {step === "form" && baseData && (
        <div style={{ display: "grid", gap: 24 }}>
          <section style={{ border: "1px solid", borderRadius: 12, padding: 16, background: "#fff" }}>
            <h2 style={{ marginTop: 0 }}>Go2Zero / CarbonPulse Daten</h2>
            <ul>
              <li>Unternehmen: <b>{company ?? "-"}</b></li>
              <li>Dateityp: <b>{fileType?.type ?? "-"}</b></li>
              {fileType?.type !== "CarbonPulse" && (
                <li>Referenzjahr: <b>{baseData.referenzjahr ?? "-"}</b></li>
              )}
            </ul>
          </section>

          {/* Progressbar nur für CarbonPulse */}
          {fileType?.type === "CarbonPulse" && (() => {
            const years = baseData?.availableYears ?? [];
            const labels = [...years, "Übersicht"];
            return (
              <div style={{ display: "flex", alignItems: "center", padding: "0 1rem" }}>
                {labels.map((label, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", flex: i < labels.length - 1 ? 1 : 0 }}>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "0.25rem" }}>
                      <span style={{ fontSize: "0.75rem", fontWeight: 600, color: i <= currentStep ? "#0065b3" : "#94a3b8" }}>
                        {label}
                      </span>
                      <div style={{
                        width: 24, height: 24, borderRadius: "50%",
                        background: "#fff",
                        border: i <= currentStep ? "2px solid #0065b3" : "2px solid #cbd5e1",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        cursor: i < currentStep ? "pointer" : "default"
                      }}
                        onClick={() => { if (i < currentStep) setCurrentStep(i); }}
                      >
                        {i < currentStep && (
                          <svg width="12" height="12" viewBox="0 0 12 12">
                            <path d="M2 6l3 3 5-5" stroke="#0065b3" strokeWidth="2" fill="none" strokeLinecap="round" />
                          </svg>
                        )}
                        {i === currentStep && (
                          <div style={{ width: 10, height: 10, borderRadius: "50%", background: "#0065b3" }} />
                        )}
                      </div>
                    </div>
                    {i < labels.length - 1 && (
                      <div style={{ flex: 1, height: 2, marginTop: "1.25rem", background: i < currentStep ? "#0065b3" : "#cbd5e1" }} />
                    )}
                  </div>
                ))}
              </div>
            );
          })()}

          <section style={{ border: "1px solid #e5e7eb", borderRadius: 12, padding: 16, background: "#fff" }}>
            {/* CarbonPulse: pro Jahr ein Formular */}
            {fileType?.type === "CarbonPulse" ? (() => {
              const years = baseData?.availableYears ?? [];
              const y = years[currentStep];

              if (!y) return null; // Sicherheit

              return (
                <>
                  <h2 style={{ marginTop: 0 }}>Daten für {y}</h2>

                  {/* ---- ALLGEMEIN ---- */}
                  <h3 className="h3" style={{ marginTop: "1rem" }}>Allgemein</h3>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 24px", marginBottom: 16 }}>
                    <sdx-select multiple label="Bereiche" placeholder="Auswählen…"
                      onInput={(e) => {
                        const raw = e?.detail?.value ?? e?.target?.value ?? [];
                        const arr = Array.isArray(raw) ? raw : [raw];
                        const vals = arr.map(x => typeof x === "object" ? x.value ?? x.label ?? "" : String(x)).filter(Boolean);
                        setFormMissingByYear(prev => ({ ...prev, [y]: { ...prev[y], bereiche: vals } }));
                      }}>
                      <sdx-select-option value="Karosserie und Lackbetrieb">Karosserie und Lackbetrieb</sdx-select-option>
                      <sdx-select-option value="Tanken">Tanken</sdx-select-option>
                      <sdx-select-option value="Waschen">Waschen</sdx-select-option>
                    </sdx-select>

                    <sdx-input label="Mitarbeitende" type="number"
                      value={formMissingByYear?.[y]?.mitarbeitende ?? ""}
                      onInput={(e) => setFormMissingByYear(prev => ({ ...prev, [y]: { ...prev[y], mitarbeitende: e.target.value } }))} />

                    <sdx-input label="Produktive Mitarbeitende" type="number"
                      value={formMissingByYear?.[y]?.produktiveMitarbeitende ?? ""}
                      onInput={(e) => setFormMissingByYear(prev => ({ ...prev, [y]: { ...prev[y], produktiveMitarbeitende: e.target.value } }))} />

                    <sdx-input label="Anzahl Reparaturen" type="number"
                      value={formMissingByYear?.[y]?.reparaturen ?? ""}
                      onInput={(e) => setFormMissingByYear(prev => ({ ...prev, [y]: { ...prev[y], reparaturen: e.target.value } }))} />

                    <sdx-input label="Verkaufte Stunden" type="number"
                      value={formMissingByYear?.[y]?.stunden ?? ""}
                      onInput={(e) => setFormMissingByYear(prev => ({ ...prev, [y]: { ...prev[y], stunden: e.target.value } }))} />
                  </div>

                  {/* ---- STROM ---- */}
                  <h3 className="h3" style={{ marginTop: "1rem" }}>Strom</h3>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 24px", marginBottom: 16 }}>
                    <div>
                      <sdx-select label="Eigene PV Anlage?" placeholder="Auswählen…"
                        onInput={(e) => {
                          const str = readSdxValue(e);
                          setFormMissingByYear(prev => ({ ...prev, [y]: { ...prev[y], pvOwn: str } }));
                        }}>
                        <sdx-select-option value="Ja" selected={formMissingByYear?.[y]?.pvOwn === "Ja"}>Ja</sdx-select-option>
                        <sdx-select-option value="Nein" selected={formMissingByYear?.[y]?.pvOwn === "Nein"}>Nein</sdx-select-option>
                      </sdx-select>
                      {formMissingByYear?.[y]?.pvOwn === "Ja" && (
                        <div style={{ marginTop: "0.5rem" }}>
                          <sdx-input label="PV-Menge (kWh)" type="number"
                            value={formMissingByYear?.[y]?.pvMengeKwh ?? ""}
                            onInput={(e) => setFormMissingByYear(prev => ({ ...prev, [y]: { ...prev[y], pvMengeKwh: e.target.value } }))} />
                        </div>
                      )}
                    </div>
                    <div>
                      <sdx-select label="Strommix" placeholder="Auswählen…"
                        onInput={(e) => {
                          const str = readSdxValue(e);
                          setFormMissingByYear(prev => ({ ...prev, [y]: { ...prev[y], strommix: str } }));
                        }}>
                        <sdx-select-option value="CH-Mix" selected={formMissingByYear?.[y]?.strommix === "CH-Mix"}>CH-Mix</sdx-select-option>
                        <sdx-select-option value="x% erneuerbar" selected={formMissingByYear?.[y]?.strommix === "x% erneuerbar"}>x% erneuerbar</sdx-select-option>
                      </sdx-select>
                      {formMissingByYear?.[y]?.strommix === "x% erneuerbar" && (
                        <div style={{ marginTop: "0.5rem" }}>
                          <label style={{ display: "block", marginBottom: "0.25rem", fontSize: "0.875rem" }}>Anteil erneuerbar (%)</label>
                          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                            <input type="range" min="0" max="100" step="1"
                              value={formMissingByYear?.[y]?.renewableShare ?? 0}
                              onInput={(e) => setFormMissingByYear(prev => ({ ...prev, [y]: { ...prev[y], renewableShare: Number(e.target.value) } }))}
                              style={{ flex: 1 }} />
                            <span style={{ width: "3.5ch", textAlign: "right", fontSize: "0.875rem" }}>{formMissingByYear?.[y]?.renewableShare ?? 0}%</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* ---- EINKAUF & ABFALL ---- */}
                  <h3 className="h3" style={{ marginTop: "1rem" }}>Einkauf & Abfall</h3>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 24px" }}>
                    <sdx-input label="Abfall (t/Jahr)" type="number"
                      value={formMissingByYear?.[y]?.abfallTonnen ?? ""}
                      onInput={(e) => setFormMissingByYear(prev => ({ ...prev, [y]: { ...prev[y], abfallTonnen: e.target.value } }))} />
                    <sdx-input label="Lack (CHF/Jahr)" type="number"
                      value={formMissingByYear?.[y]?.verbrauchsmaterialLack ?? ""}
                      onInput={(e) => setFormMissingByYear(prev => ({ ...prev, [y]: { ...prev[y], verbrauchsmaterialLack: e.target.value } }))} />
                    <sdx-input label="Verbrauchsmaterialien (CHF/Jahr)" type="number"
                      value={formMissingByYear?.[y]?.verbrauchsmaterialAllg ?? ""}
                      onInput={(e) => setFormMissingByYear(prev => ({ ...prev, [y]: { ...prev[y], verbrauchsmaterialAllg: e.target.value } }))} />
                    <sdx-input label="Ersatzteile (CHF/Jahr)" type="number"
                      value={formMissingByYear?.[y]?.verbrauchsmaterialErsatzteile ?? ""}
                      onInput={(e) => setFormMissingByYear(prev => ({ ...prev, [y]: { ...prev[y], verbrauchsmaterialErsatzteile: e.target.value } }))} />
                  </div>

                  {/* ---- BETRIEBSGRÖSSE ---- */}
                  <h3 className="h3" style={{ marginTop: "1.5rem" }}>Betriebsgrösse</h3>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 24px" }}>
                    <sdx-input label="Betriebsgrösse (m²)" type="number"
                      value={formMissingByYear?.[y]?.betriebsgrösse ?? ""}
                      onInput={(e) => setFormMissingByYear(prev => ({ ...prev, [y]: { ...prev[y], betriebsgrösse: e.target.value } }))} />
                  </div>

                  {/* ---- LACKMIX ---- */}
                  <h3 className="h3" style={{ marginTop: "1.5rem" }}>Lack</h3>
                  <sdx-select label="Lackmix" placeholder="Auswählen…"
                    onInput={(e) => {
                      const str = readSdxValue(e);
                      setFormMissingByYear(prev => ({ ...prev, [y]: { ...prev[y], lackmix: str } }));
                    }}>
                    <sdx-select-option value="konventioneller Lack" selected={formMissingByYear?.[y]?.lackmix === "konventioneller Lack"}>konventioneller Lack</sdx-select-option>
                    <sdx-select-option value="x% lufttrocknender Lack" selected={formMissingByYear?.[y]?.lackmix === "x% lufttrocknender Lack"}>x% lufttrocknender Lack</sdx-select-option>
                  </sdx-select>
                  {formMissingByYear?.[y]?.lackmix === "x% lufttrocknender Lack" && (
                    <div style={{ marginTop: "0.5rem" }}>
                      <label style={{ display: "block", marginBottom: "0.5rem" }}>Anteil lufttrocknender Lack (%)</label>
                      <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
                        <input type="range" min="0" max="100" step="1"
                          value={formMissingByYear?.[y]?.lufttrocknendShare ?? 0}
                          onInput={(e) => setFormMissingByYear(prev => ({ ...prev, [y]: { ...prev[y], lufttrocknendShare: Number(e.target.value) } }))}
                          style={{ flex: 1 }} />
                        <span style={{ width: "3.5ch", textAlign: "right" }}>{formMissingByYear?.[y]?.lufttrocknendShare ?? 0}%</span>
                      </div>
                    </div>
                  )}

                  {/* ---- TREIBSTOFF ---- */}
                  <h3 className="h3" style={{ marginTop: "1.5rem" }}>Treibstoff / Elektrische Fahrzeuge</h3>
                  <div className="table table--responsive">
                    <div className="table__wrapper">
                      <table>
                        <thead><tr><th>Typ</th><th>Einheit</th><th>Menge</th><th></th></tr></thead>
                        <tbody>
                          {getTreibstoffRows(y).map(r => (
                            <tr key={r.id}>
                              <td>
                                <sdx-select placeholder="Auswählen…"
                                  onInput={(e) => {
                                    const str = readSdxValue(e);
                                    if (str === "Diesel" || str === "Benzin") updateTreibstoffByYear(y, prev => prev.map(x => x.id === r.id ? { ...x, type: str, unit: "l" } : x));
                                    else if (str === "Elektrisch") updateTreibstoffByYear(y, prev => prev.map(x => x.id === r.id ? { ...x, type: str, unit: "kWh" } : x));
                                    else updateTreibstoffByYear(y, prev => prev.map(x => x.id === r.id ? { ...x, type: str } : x));
                                  }}>
                                  <sdx-select-option value="Diesel" selected={r.type === "Diesel"}>Diesel</sdx-select-option>
                                  <sdx-select-option value="Benzin" selected={r.type === "Benzin"}>Benzin</sdx-select-option>
                                  <sdx-select-option value="Elektrisch" selected={r.type === "Elektrisch"}>Elektrisch</sdx-select-option>
                                </sdx-select>
                              </td>
                              <td>
                                <sdx-select placeholder="Auswählen…"
                                  onInput={(e) => updateTreibstoffByYear(y, prev => prev.map(x => x.id === r.id ? { ...x, unit: readSdxValue(e) } : x))}>
                                  <sdx-select-option value="l" selected={r.unit === "l"} disabled={r.type === "Elektrisch"}>l</sdx-select-option>
                                  <sdx-select-option value="kWh" selected={r.unit === "kWh"} disabled={r.type === "Diesel" || r.type === "Benzin"}>kWh</sdx-select-option>
                                </sdx-select>
                              </td>
                              <td>
                                <sdx-input type="number" placeholder="Menge" value={r.amount ?? ""}
                                  onInput={(e) => updateTreibstoffByYear(y, prev => prev.map(x => x.id === r.id ? { ...x, amount: e.target.value } : x))} />
                              </td>
                              <td className="text-align-right">
                                <sdx-button theme="cancel" variant="ghost" label="Löschen"
                                  onClick={() => updateTreibstoffByYear(y, prev => prev.filter(x => x.id !== r.id))} />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <sdx-button theme="secondary" label="Zeile hinzufügen" style={{ marginTop: '1rem' }}
                    onClick={() => updateTreibstoffByYear(y, prev => [...prev, { id: Date.now(), type: "", unit: "", amount: "" }])}></sdx-button>

                  {/* ---- HEIZUNG ---- */}
                  <h3 className="h3" style={{ marginTop: "1.5rem" }}>Heizung</h3>
                  <div className="table table--responsive">
                    <div className="table__wrapper">
                      <table>
                        <thead><tr><th>Bereich</th><th>Energieträger</th><th>Einheit</th><th>Menge</th><th></th></tr></thead>
                        <tbody>
                          {getHeizungRows(y).map(r => (
                            <tr key={r.id}>
                              <td>
                                <sdx-select placeholder="Auswählen…"
                                  onInput={(e) => updateHeizungByYear(y, prev => prev.map(x => x.id === r.id ? { ...x, area: readSdxValue(e) } : x))}>
                                  <sdx-select-option value="Heizung" selected={r.area === "Heizung"}>Heizung</sdx-select-option>
                                  <sdx-select-option value="Lackierkabinen" selected={r.area === "Lackierkabinen"}>Lackierkabinen</sdx-select-option>
                                </sdx-select>
                              </td>
                              <td>
                                <sdx-select placeholder="Auswählen…"
                                  onInput={(e) => {
                                    const str = readSdxValue(e);
                                    if (str === "Gas") updateHeizungByYear(y, prev => prev.map(x => x.id === r.id ? { ...x, carrier: str, unit: "" } : x));
                                    else if (str === "Öl") updateHeizungByYear(y, prev => prev.map(x => x.id === r.id ? { ...x, carrier: str, unit: "l" } : x));
                                    else if (str === "Holz") updateHeizungByYear(y, prev => prev.map(x => x.id === r.id ? { ...x, carrier: str, unit: "kg" } : x));
                                    else if (str === "Fernwärme") updateHeizungByYear(y, prev => prev.map(x => x.id === r.id ? { ...x, carrier: str, unit: "kWh" } : x));
                                    else updateHeizungByYear(y, prev => prev.map(x => x.id === r.id ? { ...x, carrier: str } : x));
                                  }}>
                                  <sdx-select-option value="Gas" selected={r.carrier === "Gas"}>Gas</sdx-select-option>
                                  <sdx-select-option value="Öl" selected={r.carrier === "Öl"}>Öl</sdx-select-option>
                                  <sdx-select-option value="Holz" selected={r.carrier === "Holz"}>Holz</sdx-select-option>
                                  <sdx-select-option value="Fernwärme" selected={r.carrier === "Fernwärme"}>Fernwärme</sdx-select-option>
                                </sdx-select>
                              </td>
                              <td>
                                <sdx-select placeholder="Auswählen…"
                                  onInput={(e) => updateHeizungByYear(y, prev => prev.map(x => x.id === r.id ? { ...x, unit: readSdxValue(e) } : x))}>
                                  <sdx-select-option value="kWh" selected={r.unit === "kWh"} disabled={r.carrier === "Öl" || r.carrier === "Holz"}>kWh</sdx-select-option>
                                  <sdx-select-option value="l" selected={r.unit === "l"} disabled={r.carrier === "Gas" || r.carrier === "Holz" || r.carrier === "Fernwärme"}>l</sdx-select-option>
                                  <sdx-select-option value="kg" selected={r.unit === "kg"} disabled={r.carrier === "Gas" || r.carrier === "Öl" || r.carrier === "Fernwärme"}>kg</sdx-select-option>
                                  <sdx-select-option value="m3" selected={r.unit === "m3"} disabled={r.carrier === "Öl" || r.carrier === "Holz" || r.carrier === "Fernwärme"}>m3</sdx-select-option>
                                </sdx-select>
                              </td>
                              <td>
                                <sdx-input type="number" placeholder="Menge" value={r.amount ?? ""}
                                  onInput={(e) => updateHeizungByYear(y, prev => prev.map(x => x.id === r.id ? { ...x, amount: e.target.value } : x))} />
                              </td>
                              <td className="text-align-right">
                                <sdx-button theme="cancel" variant="ghost" label="Löschen"
                                  onClick={() => updateHeizungByYear(y, prev => prev.filter(x => x.id !== r.id))} />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <sdx-button theme="secondary" label="Zeile hinzufügen" style={{ marginTop: '1rem' }}
                    onClick={() => updateHeizungByYear(y, prev => [...prev, { id: Date.now(), area: "", carrier: "", unit: "", amount: "" }])}></sdx-button>

                  {/* ---- WÄRMEPUMPE ---- */}
                  <div style={{ marginTop: '1rem' }}>
                    <label>Besitzen Sie eine Wärmepumpe?</label>
                    <sdx-menu-flyout>
                      <sdx-menu-flyout-toggle sr-hint="Mehr Infos"><sdx-icon icon-name="icon-information-circle"></sdx-icon></sdx-menu-flyout-toggle>
                      <sdx-menu-flyout-content style={{ width: '300px' }}>Der Verbrauch der Wärmepumpe ist im Punkt Strom inkludiert.</sdx-menu-flyout-content>
                    </sdx-menu-flyout>
                    <sdx-select placeholder="Auswählen…"
                      onInput={(e) => setFormMissingByYear(prev => ({ ...prev, [y]: { ...prev[y], wärmepumpe: readSdxValue(e) } }))}>
                      <sdx-select-option value="Ja" selected={formMissingByYear?.[y]?.wärmepumpe === "Ja"}>Ja</sdx-select-option>
                      <sdx-select-option value="Nein" selected={formMissingByYear?.[y]?.wärmepumpe === "Nein"}>Nein</sdx-select-option>
                    </sdx-select>
                    {formMissingByYear?.[y]?.wärmepumpe === "Ja" && (
                      <sdx-select label="Für welche Bereiche?" placeholder="Auswählen…"
                        onInput={(e) => setFormMissingByYear(prev => ({ ...prev, [y]: { ...prev[y], wärmepumpeBereiche: readSdxValue(e) } }))}
                        style={{ marginTop: '1rem' }}>
                        <sdx-select-option value="Heizung" selected={formMissingByYear?.[y]?.wärmepumpeBereiche === "Heizung"}>Heizung</sdx-select-option>
                        <sdx-select-option value="Lackierkabine" selected={formMissingByYear?.[y]?.wärmepumpeBereiche === "Lackierkabine"}>Lackierkabine</sdx-select-option>
                        <sdx-select-option value="Beides" selected={formMissingByYear?.[y]?.wärmepumpeBereiche === "Beides"}>Beides</sdx-select-option>
                      </sdx-select>
                    )}
                  </div>

                  {/* ---- LACKIERKABINEN ---- */}
                  <h3 className="h3" style={{ marginTop: "1.5rem" }}>Lackierkabinen</h3>
                  <div className="table table--responsive">
                    <div className="table__wrapper">
                      <table>
                        <thead><tr><th>Art</th><th></th></tr></thead>
                        <tbody>
                          {getLackierkabinenRows(y).map(r => (
                            <tr key={r.id}>
                              <td>
                                <sdx-select placeholder="Art auswählen…"
                                  onInput={(e) => updateLackierkabinenByYear(y, prev => prev.map(x => x.id === r.id ? { ...x, area: readSdxValue(e) } : x))}>
                                  <sdx-select-option value="Alte Einbrennkabinen mit Gasbrenner und veralteter Steuertechnik" selected={r.area === "Alte Einbrennkabinen mit Gasbrenner und veralteter Steuertechnik"}>Alte Einbrennkabinen mit Gasbrenner und veralteter Steuertechnik</sdx-select-option>
                                  <sdx-select-option value="Moderne Kabinen mit Wärmererückgewinnung" selected={r.area === "Moderne Kabinen mit Wärmererückgewinnung"}>Moderne Kabinen mit Wärmererückgewinnung</sdx-select-option>
                                  <sdx-select-option value="Moderne Kabinen mit Gasbrenner" selected={r.area === "Moderne Kabinen mit Gasbrenner"}>Moderne Kabinen mit Gasbrenner</sdx-select-option>
                                  <sdx-select-option value="Moderne Kabinen mit Gas- und Infrarot-Technik fix in den Wänden verbaut" selected={r.area === "Moderne Kabinen mit Gas- und Infrarot-Technik fix in den Wänden verbaut"}>Moderne Kabinen mit Gas- und Infrarot-Technik fix in den Wänden verbaut</sdx-select-option>
                                  <sdx-select-option value="Moderne Kabinen mit Elektroheizung" selected={r.area === "Moderne Kabinen mit Elektroheizung"}>Moderne Kabinen mit Elektroheizung</sdx-select-option>
                                  <sdx-select-option value="Moderne Kabinen (Vorbereitung) mit beweglichen UV/Infrarot Armausleger" selected={r.area === "Moderne Kabinen (Vorbereitung) mit beweglichen UV/Infrarot Armausleger"}>Moderne Kabinen (Vorbereitung) mit beweglichen UV/Infrarot Armausleger</sdx-select-option>
                                  <sdx-select-option value="Grosse und kleine Kabinen" selected={r.area === "Grosse und kleine Kabinen"}>Grosse und kleine Kabinen</sdx-select-option>
                                  <sdx-select-option value="Kombikabinen mit Trennwänden" selected={r.area === "Kombikabinen mit Trennwänden"}>Kombikabinen mit Trennwänden</sdx-select-option>
                                </sdx-select>
                              </td>
                              <td className="text-align-right">
                                <sdx-button theme="cancel" variant="ghost" label="Löschen"
                                  onClick={() => updateLackierkabinenByYear(y, prev => prev.filter(x => x.id !== r.id))} />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <sdx-button theme="secondary" label="Zeile hinzufügen" style={{ marginTop: '1rem' }}
                    onClick={() => updateLackierkabinenByYear(y, prev => [...prev, { id: Date.now(), area: "" }])}></sdx-button>

                  {/* ---- FOSSILE FAHRZEUGE ---- */}
                  <h3 className="h3" style={{ marginTop: "1.5rem" }}>Fossile Fahrzeuge</h3>
                  <div className="table table--responsive">
                    <div className="table__wrapper">
                      <table>
                        <thead><tr><th>Treibstofftyp</th><th>Anzahl</th><th></th></tr></thead>
                        <tbody>
                          {getFossilRows(y).map(r => (
                            <tr key={r.id}>
                              <td>
                                <sdx-select placeholder="Auswählen…"
                                  onInput={(e) => updateFossilByYear(y, prev => prev.map(x => x.id === r.id ? { ...x, type: readSdxValue(e) } : x))}>
                                  <sdx-select-option value="Diesel" selected={r.type === "Diesel"}>Diesel</sdx-select-option>
                                  <sdx-select-option value="Benzin" selected={r.type === "Benzin"}>Benzin</sdx-select-option>
                                </sdx-select>
                              </td>
                              <td>
                                <sdx-input type="number" placeholder="Anzahl" value={r.amount ?? ""}
                                  onInput={(e) => updateFossilByYear(y, prev => prev.map(x => x.id === r.id ? { ...x, amount: e.target.value } : x))} />
                              </td>
                              <td className="text-align-right">
                                <sdx-button theme="cancel" variant="ghost" label="Löschen"
                                  onClick={() => updateFossilByYear(y, prev => prev.filter(x => x.id !== r.id))} />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <sdx-button theme="secondary" label="Zeile hinzufügen" style={{ marginTop: '1rem' }}
                    onClick={() => updateFossilByYear(y, prev => [...prev, { id: Date.now(), type: "", amount: "" }])}></sdx-button>

                  {/* ---- BELEUCHTUNG ---- */}
                  <h3 className="h3" style={{ marginTop: "1.5rem" }}>Beleuchtung</h3>
                  <div className="table table--responsive">
                    <div className="table__wrapper">
                      <table>
                        <thead><tr><th>Beleuchtungstyp</th><th>Anzahl</th><th></th></tr></thead>
                        <tbody>
                          {getLightRows(y).map(r => (
                            <tr key={r.id}>
                              <td>
                                <sdx-select placeholder="Auswählen…"
                                  onInput={(e) => updateLightByYear(y, prev => prev.map(x => x.id === r.id ? { ...x, type: readSdxValue(e) } : x))}>
                                  <sdx-select-option value="LED" selected={r.type === "LED"}>LED</sdx-select-option>
                                  <sdx-select-option value="Halogen" selected={r.type === "Halogen"}>Halogen</sdx-select-option>
                                  <sdx-select-option value="Glühlampe" selected={r.type === "Glühlampe"}>Glühlampe</sdx-select-option>
                                </sdx-select>
                              </td>
                              <td>
                                <sdx-input type="number" placeholder="Anzahl" value={r.amount ?? ""}
                                  onInput={(e) => updateLightByYear(y, prev => prev.map(x => x.id === r.id ? { ...x, amount: e.target.value } : x))} />
                              </td>
                              <td className="text-align-right">
                                <sdx-button theme="cancel" variant="ghost" label="Löschen"
                                  onClick={() => updateLightByYear(y, prev => prev.filter(x => x.id !== r.id))} />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <sdx-button theme="secondary" label="Zeile hinzufügen" style={{ marginTop: '1rem' }}
                    onClick={() => updateLightByYear(y, prev => [...prev, { id: Date.now(), type: "", amount: "" }])}></sdx-button>

                  {/* Navigation */}
                  <div style={{ display: "flex", gap: 12, marginTop: 24 }}>
                    <sdx-button theme="secondary" label="Zurück zum Upload"
                      onClick={() => { setStep("upload"); setBaseData(null); setData(null); setCurrentStep(0); }} />
                    {currentStep > 0 && (
                      <sdx-button theme="secondary" label={`Zurück zu ${years[currentStep - 1]}`}
                        onClick={() => { setCurrentStep(s => s - 1); setStep("form"); window.scrollTo(0, 0); }} />
                    )}
                    <sdx-button variant="primary"
                      label={currentStep < years.length - 1 ? `Weiter zu ${years[currentStep + 1]}` : "Zur Übersicht"}
                      onClick={() => {
                        if (currentStep < years.length - 1) {
                          setCurrentStep(s => s + 1);
                          setStep("form"); window.scrollTo(0, 0);
                        } else {
                          handleSaveForm();
                        }
                      }} />
                  </div>
                </>
              );
            })() : (
              // Go2Zero: bisheriges Formular bleibt gleich
              <>
                <h2 style={{ marginTop: 0 }}>Fehlende Daten ergänzen</h2>
                {/* ---- ALLGEMEIN ---- */}
                <h3 className="h3" style={{ marginTop: "1rem" }}>Allgemein</h3>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 24px", marginBottom: 16 }}>
                  <sdx-select multiple label="Bereiche" placeholder="Auswählen…"
                    onInput={(e) => {
                      const raw = e?.detail?.value ?? e?.target?.value ?? [];
                      const arr = Array.isArray(raw) ? raw : [raw];
                      const vals = arr.map(x => typeof x === "object" ? x.value ?? x.label ?? "" : String(x)).filter(Boolean);
                      setFormMissing(p => ({ ...p, bereiche: vals }));
                    }}>
                    <sdx-select-option value="Karosserie und Lackbetrieb">Karosserie und Lackbetrieb</sdx-select-option>
                    <sdx-select-option value="Tanken">Tanken</sdx-select-option>
                    <sdx-select-option value="Waschen">Waschen</sdx-select-option>
                  </sdx-select>
                  <sdx-input label="Mitarbeitende" type="number" value={formMissing.mitarbeitende ?? ""}
                    onInput={(e) => setFormMissing(p => ({ ...p, mitarbeitende: e.target.value }))} />
                  <sdx-input label="Produktive Mitarbeitende" type="number" value={formMissing.produktiveMitarbeitende ?? ""}
                    onInput={(e) => setFormMissing(p => ({ ...p, produktiveMitarbeitende: e.target.value }))} />
                  <sdx-input label="Anzahl Reparaturen" type="number" value={formMissing.reparaturen ?? ""}
                    onInput={(e) => setFormMissing(p => ({ ...p, reparaturen: e.target.value }))} />
                  <sdx-input label="Verkaufte Stunden" type="number" value={formMissing.stunden ?? ""}
                    onInput={(e) => setFormMissing(p => ({ ...p, stunden: e.target.value }))} />
                </div>

                {/* Strom */}
                <h3 className="h3" style={{ marginTop: "1rem" }}>Strom</h3>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 24px", marginBottom: 16 }}>
                  <div>
                    <sdx-select label="Eigene PV Anlage?" placeholder="Auswählen…"
                      onInput={(e) => setFormMissing(p => ({ ...p, pvOwn: readSdxValue(e) }))}>
                      <sdx-select-option value="Ja" selected={formMissing.pvOwn === "Ja"}>Ja</sdx-select-option>
                      <sdx-select-option value="Nein" selected={formMissing.pvOwn === "Nein"}>Nein</sdx-select-option>
                    </sdx-select>
                    {formMissing.pvOwn === "Ja" && (
                      <div style={{ marginTop: "0.5rem" }}>
                        <sdx-input label="PV-Menge (kWh)" type="number" value={formMissing.pvMengeKwh ?? ""}
                          onInput={(e) => setFormMissing(p => ({ ...p, pvMengeKwh: e.target.value }))} />
                      </div>
                    )}
                  </div>
                  <div>
                    <sdx-select label="Strommix" placeholder="Auswählen…"
                      onInput={(e) => setFormMissing(p => ({ ...p, strommix: readSdxValue(e) }))}>
                      <sdx-select-option value="CH-Mix" selected={formMissing.strommix === "CH-Mix"}>CH-Mix</sdx-select-option>
                      <sdx-select-option value="x% erneuerbar" selected={formMissing.strommix === "x% erneuerbar"}>x% erneuerbar</sdx-select-option>
                    </sdx-select>
                    {formMissing.strommix === "x% erneuerbar" && (
                      <div style={{ marginTop: "0.5rem" }}>
                        <label style={{ display: "block", marginBottom: "0.25rem", fontSize: "0.875rem" }}>Anteil erneuerbar (%)</label>
                        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                          <input type="range" min="0" max="100" step="1"
                            value={formMissing.renewableShare ?? 0}
                            onInput={(e) => setFormMissing(p => ({ ...p, renewableShare: Number(e.target.value) }))}
                            style={{ flex: 1 }} />
                          <span style={{ width: "3.5ch", textAlign: "right", fontSize: "0.875rem" }}>{formMissing.renewableShare ?? 0}%</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Einkauf & Abfall */}
                <h3 className="h3" style={{ marginTop: "1rem" }}>Einkauf & Abfall</h3>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 24px" }}>
                  <sdx-input label="Abfall (t/Jahr)" type="number" value={formMissing.abfallTonnen ?? ""}
                    onInput={(e) => setFormMissing(p => ({ ...p, abfallTonnen: e.target.value }))} />
                  <sdx-input label="Lack (CHF/Jahr)" type="number" value={formMissing.verbrauchsmaterialLack ?? ""}
                    onInput={(e) => setFormMissing(p => ({ ...p, verbrauchsmaterialLack: e.target.value }))} />
                  <sdx-input label="Verbrauchsmaterialien (CHF/Jahr)" type="number" value={formMissing.verbrauchsmaterialAllg ?? ""}
                    onInput={(e) => setFormMissing(p => ({ ...p, verbrauchsmaterialAllg: e.target.value }))} />
                  <sdx-input label="Ersatzteile (CHF/Jahr)" type="number" value={formMissing.verbrauchsmaterialErsatzteile ?? ""}
                    onInput={(e) => setFormMissing(p => ({ ...p, verbrauchsmaterialErsatzteile: e.target.value }))} />
                </div>

                {/* Betriebsgrösse, Lack, Treibstoff, Heizung, Lackierkabinen, Fossile, Beleuchtung - gleich wie vorher */}
                <h3 className="h3" style={{ marginTop: "1.5rem" }}>Betriebsgrösse</h3>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 24px" }}>
                  <sdx-input label="Betriebsgrösse (m²)" type="number" value={formMissing.betriebsgrösse ?? ""}
                    onInput={(e) => setFormMissing(p => ({ ...p, betriebsgrösse: e.target.value }))} />
                </div>

                <h3 className="h3" style={{ marginTop: "1.5rem" }}>Lack</h3>
                <sdx-select label="Lackmix" placeholder="Auswählen…"
                  onInput={(e) => setFormMissing(p => ({ ...p, lackmix: readSdxValue(e) }))}>
                  <sdx-select-option value="konventioneller Lack" selected={formMissing.lackmix === "konventioneller Lack"}>konventioneller Lack</sdx-select-option>
                  <sdx-select-option value="x% lufttrocknender Lack" selected={formMissing.lackmix === "x% lufttrocknender Lack"}>x% lufttrocknender Lack</sdx-select-option>
                </sdx-select>
                {formMissing.lackmix === "x% lufttrocknender Lack" && (
                  <div style={{ marginTop: "0.5rem" }}>
                    <label style={{ display: "block", marginBottom: "0.5rem" }}>Anteil lufttrocknender Lack (%)</label>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
                      <input type="range" min="0" max="100" step="1"
                        value={formMissing.lufttrocknendShare ?? 0}
                        onInput={(e) => setFormMissing(p => ({ ...p, lufttrocknendShare: Number(e.target.value) }))}
                        style={{ flex: 1 }} />
                      <span style={{ width: "3.5ch", textAlign: "right" }}>{formMissing.lufttrocknendShare ?? 0}%</span>
                    </div>
                  </div>
                )}

                {/* Treibstoff, Heizung, Wärmepumpe, Lackierkabinen, Fossile, Beleuchtung - gleich wie bisheriger Go2Zero Code */}
                <h3 className="h3" style={{ marginTop: "1.5rem" }}>Treibstoff / Elektrische Fahrzeuge</h3>
                <div className="table table--responsive"><div className="table__wrapper"><table>
                  <thead><tr><th>Typ</th><th>Einheit</th><th>Menge</th><th></th></tr></thead>
                  <tbody>{treibstoffRows.map(r => (
                    <tr key={r.id}>
                      <td><sdx-select placeholder="Auswählen…" onInput={(e) => { const str = readSdxValue(e); if (str === "Diesel" || str === "Benzin") updateTreibstoffRow(r.id, { type: str, unit: "l" }); else if (str === "Elektrisch") updateTreibstoffRow(r.id, { type: str, unit: "kWh" }); else updateTreibstoffRow(r.id, { type: str }); }}><sdx-select-option value="Diesel" selected={r.type === "Diesel"}>Diesel</sdx-select-option><sdx-select-option value="Benzin" selected={r.type === "Benzin"}>Benzin</sdx-select-option><sdx-select-option value="Elektrisch" selected={r.type === "Elektrisch"}>Elektrisch</sdx-select-option></sdx-select></td>
                      <td><sdx-select placeholder="Auswählen…" onInput={(e) => updateTreibstoffRow(r.id, { unit: readSdxValue(e) })}><sdx-select-option value="l" selected={r.unit === "l"} disabled={r.type === "Elektrisch"}>l</sdx-select-option><sdx-select-option value="kWh" selected={r.unit === "kWh"} disabled={r.type === "Diesel" || r.type === "Benzin"}>kWh</sdx-select-option></sdx-select></td>
                      <td><sdx-input type="number" placeholder="Menge" value={r.amount ?? ""} onInput={(e) => updateTreibstoffRow(r.id, { amount: e.target.value })} /></td>
                      <td className="text-align-right"><sdx-button theme="cancel" variant="ghost" label="Löschen" onClick={() => removeTreibstoffRow(r.id)} /></td>
                    </tr>
                  ))}</tbody>
                </table></div></div>
                <sdx-button theme="secondary" onClick={addTreibstoffRow} label="Zeile hinzufügen" style={{ marginTop: '1rem' }}></sdx-button>

                <h3 className="h3" style={{ marginTop: "1.5rem" }}>Heizung</h3>
                <div className="table table--responsive"><div className="table__wrapper"><table>
                  <thead><tr><th>Bereich</th><th>Energieträger</th><th>Einheit</th><th>Menge</th><th></th></tr></thead>
                  <tbody>{heizungRows.map(r => (
                    <tr key={r.id}>
                      <td><sdx-select placeholder="Auswählen…" onInput={(e) => updateHeizungRow(r.id, { area: readSdxValue(e) })}><sdx-select-option value="Heizung" selected={r.area === "Heizung"}>Heizung</sdx-select-option><sdx-select-option value="Lackierkabinen" selected={r.area === "Lackierkabinen"}>Lackierkabinen</sdx-select-option></sdx-select></td>
                      <td><sdx-select placeholder="Auswählen…" onInput={(e) => { const str = readSdxValue(e); if (str === "Wärmepumpe" || str === "Gas") updateHeizungRow(r.id, { carrier: str, unit: "" }); else if (str === "Öl") updateHeizungRow(r.id, { carrier: str, unit: "l" }); else if (str === "Holz") updateHeizungRow(r.id, { carrier: str, unit: "kg" }); else if (str === "Fernwärme") updateHeizungRow(r.id, { carrier: str, unit: "kWh" }); else updateHeizungRow(r.id, { carrier: str }); }}><sdx-select-option value="Gas" selected={r.carrier === "Gas"}>Gas</sdx-select-option><sdx-select-option value="Öl" selected={r.carrier === "Öl"}>Öl</sdx-select-option><sdx-select-option value="Holz" selected={r.carrier === "Holz"}>Holz</sdx-select-option><sdx-select-option value="Fernwärme" selected={r.carrier === "Fernwärme"}>Fernwärme</sdx-select-option></sdx-select></td>
                      <td><sdx-select placeholder="Auswählen…" onInput={(e) => updateHeizungRow(r.id, { unit: readSdxValue(e) })}><sdx-select-option value="kWh" selected={r.unit === "kWh"} disabled={r.carrier === "Öl" || r.carrier === "Holz"}>kWh</sdx-select-option><sdx-select-option value="l" selected={r.unit === "l"} disabled={r.carrier === "Gas" || r.carrier === "Holz" || r.carrier === "Fernwärme"}>l</sdx-select-option><sdx-select-option value="kg" selected={r.unit === "kg"} disabled={r.carrier === "Gas" || r.carrier === "Öl" || r.carrier === "Fernwärme"}>kg</sdx-select-option><sdx-select-option value="m3" selected={r.unit === "m3"} disabled={r.carrier === "Öl" || r.carrier === "Holz" || r.carrier === "Fernwärme"}>m3</sdx-select-option></sdx-select></td>
                      <td><sdx-input type="number" placeholder="Menge" value={r.amount ?? ""} onInput={(e) => updateHeizungRow(r.id, { amount: e.target.value })} /></td>
                      <td className="text-align-right"><sdx-button theme="cancel" variant="ghost" label="Löschen" onClick={() => removeHeizungRow(r.id)} /></td>
                    </tr>
                  ))}</tbody>
                </table></div></div>
                <sdx-button theme="secondary" onClick={addHeizungRow} label="Zeile hinzufügen" style={{ marginTop: '1rem' }}></sdx-button>

                <div style={{ marginTop: '1rem' }}>
                  <label>Besitzen Sie eine Wärmepumpe?</label>
                  <sdx-menu-flyout><sdx-menu-flyout-toggle sr-hint="Mehr Infos"><sdx-icon icon-name="icon-information-circle"></sdx-icon></sdx-menu-flyout-toggle><sdx-menu-flyout-content style={{ width: '300px' }}>Der Verbrauch der Wärmepumpe ist im Punkt Strom inkludiert.</sdx-menu-flyout-content></sdx-menu-flyout>
                  <sdx-select placeholder="Auswählen…" onInput={(e) => handleChange('wärmepumpe', readSdxValue(e))}><sdx-select-option value="Ja" selected={formData.wärmepumpe === "Ja"}>Ja</sdx-select-option><sdx-select-option value="Nein" selected={formData.wärmepumpe === "Nein"}>Nein</sdx-select-option></sdx-select>
                  {formData.wärmepumpe === 'Ja' && (<sdx-select label="Für welche Bereiche?" placeholder="Auswählen…" onInput={(e) => handleChange('wärmepumpeBereiche', readSdxValue(e))} style={{ marginTop: '1rem' }}><sdx-select-option value="Heizung" selected={formData.wärmepumpeBereiche === "Heizung"}>Heizung</sdx-select-option><sdx-select-option value="Lackierkabine" selected={formData.wärmepumpeBereiche === "Lackierkabine"}>Lackierkabine</sdx-select-option><sdx-select-option value="Beides" selected={formData.wärmepumpeBereiche === "Beides"}>Beides</sdx-select-option></sdx-select>)}
                </div>

                <h3 className="h3" style={{ marginTop: "1.5rem" }}>Lackierkabinen</h3>
                <div className="table table--responsive"><div className="table__wrapper"><table><thead><tr><th>Art</th><th></th></tr></thead><tbody>{lackierkabinenRows.map(r => (<tr key={r.id}><td><sdx-select placeholder="Art auswählen…" onInput={(e) => updateLackierkabinenRow(r.id, { area: readSdxValue(e) })}><sdx-select-option value="Alte Einbrennkabinen mit Gasbrenner und veralteter Steuertechnik" selected={r.area === "Alte Einbrennkabinen mit Gasbrenner und veralteter Steuertechnik"}>Alte Einbrennkabinen mit Gasbrenner und veralteter Steuertechnik</sdx-select-option><sdx-select-option value="Moderne Kabinen mit Wärmererückgewinnung" selected={r.area === "Moderne Kabinen mit Wärmererückgewinnung"}>Moderne Kabinen mit Wärmererückgewinnung</sdx-select-option><sdx-select-option value="Moderne Kabinen mit Gasbrenner" selected={r.area === "Moderne Kabinen mit Gasbrenner"}>Moderne Kabinen mit Gasbrenner</sdx-select-option><sdx-select-option value="Moderne Kabinen mit Gas- und Infrarot-Technik fix in den Wänden verbaut" selected={r.area === "Moderne Kabinen mit Gas- und Infrarot-Technik fix in den Wänden verbaut"}>Moderne Kabinen mit Gas- und Infrarot-Technik fix in den Wänden verbaut</sdx-select-option><sdx-select-option value="Moderne Kabinen mit Elektroheizung" selected={r.area === "Moderne Kabinen mit Elektroheizung"}>Moderne Kabinen mit Elektroheizung</sdx-select-option><sdx-select-option value="Moderne Kabinen (Vorbereitung) mit beweglichen UV/Infrarot Armausleger" selected={r.area === "Moderne Kabinen (Vorbereitung) mit beweglichen UV/Infrarot Armausleger"}>Moderne Kabinen (Vorbereitung) mit beweglichen UV/Infrarot Armausleger</sdx-select-option><sdx-select-option value="Grosse und kleine Kabinen" selected={r.area === "Grosse und kleine Kabinen"}>Grosse und kleine Kabinen</sdx-select-option><sdx-select-option value="Kombikabinen mit Trennwänden" selected={r.area === "Kombikabinen mit Trennwänden"}>Kombikabinen mit Trennwänden</sdx-select-option></sdx-select></td><td className="text-align-right"><sdx-button theme="cancel" variant="ghost" label="Löschen" onClick={() => removeLackierkabinenRow(r.id)} /></td></tr>))}</tbody></table></div></div>
                <sdx-button theme="secondary" onClick={addLackierkabinenRow} label="Zeile hinzufügen" style={{ marginTop: '1rem' }}></sdx-button>

                <h3 className="h3" style={{ marginTop: "1.5rem" }}>Fossile Fahrzeuge</h3>
                <div className="table table--responsive"><div className="table__wrapper"><table><thead><tr><th>Treibstofftyp</th><th>Anzahl</th><th></th></tr></thead><tbody>{fossilRows.map(r => (<tr key={r.id}><td><sdx-select placeholder="Auswählen…" onInput={(e) => updateFossilRow(r.id, { type: readSdxValue(e) })}><sdx-select-option value="Diesel" selected={r.type === "Diesel"}>Diesel</sdx-select-option><sdx-select-option value="Benzin" selected={r.type === "Benzin"}>Benzin</sdx-select-option></sdx-select></td><td><sdx-input type="number" placeholder="Anzahl" value={r.amount ?? ""} onInput={(e) => updateFossilRow(r.id, { amount: e.target.value })} /></td><td className="text-align-right"><sdx-button theme="cancel" variant="ghost" label="Löschen" onClick={() => removeFossilRow(r.id)} /></td></tr>))}</tbody></table></div></div>
                <sdx-button theme="secondary" onClick={addFossilRow} label="Zeile hinzufügen" style={{ marginTop: '1rem' }}></sdx-button>

                <h3 className="h3" style={{ marginTop: "1.5rem" }}>Beleuchtung</h3>
                <div className="table table--responsive"><div className="table__wrapper"><table><thead><tr><th>Beleuchtungstyp</th><th>Anzahl</th><th></th></tr></thead><tbody>{lightRows.map(r => (<tr key={r.id}><td><sdx-select placeholder="Auswählen…" onInput={(e) => updateLightRow(r.id, { type: readSdxValue(e) })}><sdx-select-option value="LED" selected={r.type === "LED"}>LED</sdx-select-option><sdx-select-option value="Halogen" selected={r.type === "Halogen"}>Halogen</sdx-select-option><sdx-select-option value="Glühlampe" selected={r.type === "Glühlampe"}>Glühlampe</sdx-select-option></sdx-select></td><td><sdx-input type="number" placeholder="Anzahl" value={r.amount ?? ""} onInput={(e) => updateLightRow(r.id, { amount: e.target.value })} /></td><td className="text-align-right"><sdx-button theme="cancel" variant="ghost" label="Löschen" onClick={() => removeLightRow(r.id)} /></td></tr>))}</tbody></table></div></div>
                <sdx-button theme="secondary" onClick={addLightRow} label="Zeile hinzufügen" style={{ marginTop: '1rem' }}></sdx-button>

                <div style={{ display: "flex", gap: 12, marginTop: 16 }}>
                  <sdx-button theme="secondary" label="Zurück zum Upload"
                    onClick={() => { setStep("upload"); setBaseData(null); setData(null); }} />
                  <sdx-button variant="primary" label="Auswertung anzeigen" onClick={handleSaveForm} />
                </div>
              </>
            )}
          </section>
        </div>
      )}

      {/* STEP 3: Summary */}
      {step === "summary" && display && (
        <div style={{ display: "grid", gap: 24 }}>
          {fileType?.type === "CarbonPulse" && (data?.availableYears?.length ?? 0) > 1 && (
            <div style={{ marginBottom: -8 }}>
              <label style={{ marginRight: 8, color: "#475569" }}>Jahr auswählen:</label>
              <select
                value={selectedYear ?? ""}
                onChange={(e) => setSelectedYear(Number(e.target.value))}
                style={{ padding: 6, borderRadius: 6, border: "1px solid #cbd5e1" }}
              >
                {(data.availableYears || []).map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </div>
          )}

          <section
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: 16,
            }}
          >
            <KpiCard title="File" value={fileType?.type ?? "-"} />
            <KpiCard title="Referenzjahr" value={display.referenzjahr ?? "-"} />
          </section>

          <section style={summarySectionStyle}>
            <h3 className="h3">Allgemein</h3>
            <div style={{ display: "flex", gap: "0.5rem", marginBottom: "0.25rem" }}>
              <strong style={{ minWidth: "12rem" }}>Bereiche:</strong>
              <span>{(display?.bereiche ?? []).join(", ") || "-"}</span>
            </div>
            <div style={{ display: "flex", gap: "0.5rem", marginBottom: "0.25rem" }}>
              <strong style={{ minWidth: "12rem" }}>Mitarbeitende:</strong>
              <span>{display?.mitarbeitende || "-"}</span>
            </div>
            <div style={{ display: "flex", gap: "0.5rem", marginBottom: "0.25rem" }}>
              <strong style={{ minWidth: "12rem" }}>Produktive Mitarbeitende:</strong>
              <span>{display?.produktiveMitarbeitende || "-"}</span>
            </div>
            <div style={{ display: "flex", gap: "0.5rem", marginBottom: "0.25rem" }}>
              <strong style={{ minWidth: "12rem" }}>Anzahl Reparaturen:</strong>
              <span>{display?.reparaturen || "-"}</span>
            </div>
            <div style={{ display: "flex", gap: "0.5rem", marginBottom: "0.25rem" }}>
              <strong style={{ minWidth: "12rem" }}>Verkaufte Stunden:</strong>
              <span>{display?.stunden || "-"}</span>
            </div>
            <div style={{ display: "flex", gap: "0.5rem", marginBottom: "0.25rem" }}>
              <strong style={{ minWidth: "12rem" }}>Betriebsgrösse (m²):</strong>
              <span>{display?.betriebsgrösse || "-"}</span>
            </div>
          </section>

          {/* ---- STROMMIX ---- */}
          <section style={summarySectionStyle}>
            <h3 className="h3">Strom</h3>
            <div style={{ display: "flex", gap: "0.5rem", marginBottom: "0.25rem" }}>
              <strong style={{ minWidth: "12rem" }}>Strommix:</strong>
              <span>{display?.strommix || "-"}</span>
            </div>
            {display?.strommix === "x% erneuerbar" && (
              <div style={{ display: "flex", gap: "0.5rem", marginBottom: "0.25rem" }}>
                <strong style={{ minWidth: "12rem" }}>Anteil erneuerbar (%):</strong>
                <span>{display?.renewableShare ?? "-"}%</span>
              </div>
            )}
            <div style={{ display: "flex", gap: "0.5rem", marginBottom: "0.25rem" }}>
              <strong style={{ minWidth: "12rem" }}>Menge in kWh:</strong>
              <span>{display?.mengeStrom || "-"}</span>
            </div>
            <div style={{ display: "flex", gap: "0.5rem", marginBottom: "0.25rem" }}>
              <strong style={{ minWidth: "12rem" }}>Eigene PV Anlage:</strong>
              <span>{display?.pvOwn ?? "-"}</span>
            </div>
            {display?.pvOwn === "Ja" && (
              <div style={{ display: "flex", gap: "0.5rem", marginBottom: "0.25rem" }}>
                <strong style={{ minWidth: "12rem" }}>PV-Menge (kWh):</strong>
                <span>{display?.pvMengeKwh ?? "-"}</span>
              </div>
            )}
          </section>

          {/* ---- LACK ---- */}
          <section style={summarySectionStyle}>
            <h3 className="h3">Lack</h3>
            <div style={{ display: "flex", gap: "0.5rem", marginBottom: "0.25rem" }}>
              <strong style={{ minWidth: "12rem" }}>Lackmix:</strong>
              <span>{display?.lackmix || "-"}</span>
            </div>
            {display?.lackmix === "x% lufttrocknender Lack" && (
              <div style={{ display: "flex", gap: "0.5rem", marginBottom: "0.25rem" }}>
                <strong style={{ minWidth: "12rem" }}>Anteil lufttrocknend (%):</strong>
                <span>{display?.lufttrocknendShare ?? "-"}</span>
              </div>
            )}
          </section>
          {/* ---- HEIZUNG ---- */}
          <section style={summarySectionStyle}>
            <h3 className="h3">Heizung</h3>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid #e5e7eb" }}>
                  <th style={{ textAlign: "left", padding: "4px 8px" }}>Bereich</th>
                  <th style={{ textAlign: "left", padding: "4px 8px" }}>Energieträger</th>
                  <th style={{ textAlign: "left", padding: "4px 8px" }}>Einheit</th>
                  <th style={{ textAlign: "left", padding: "4px 8px" }}>Menge</th>
                </tr>
              </thead>
              <tbody>
                {(display.heizung ?? []).map((r, i) => (
                  <tr key={i} style={{ borderBottom: "1px solid #f1f5f9" }}>
                    <td style={{ padding: "4px 8px" }}>{r.area || "-"}</td>
                    <td style={{ padding: "4px 8px" }}>{r.carrier || "-"}</td>
                    <td style={{ padding: "4px 8px" }}>{r.unit || "-"}</td>
                    <td style={{ padding: "4px 8px" }}>{r.amount !== "" ? r.amount : "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ marginTop: "1rem" }}>
              <div style={{ display: "flex", gap: "0.5rem", marginBottom: "0.25rem" }}>
                <strong style={{ minWidth: "12rem" }}>Wärmepumpe:</strong>
                <span>{display?.wärmepumpe ?? "-"}</span>
              </div>
              {display?.wärmepumpe === "Ja" && (
                <div style={{ display: "flex", gap: "0.5rem", marginBottom: "0.25rem" }}>
                  <strong style={{ minWidth: "12rem" }}>Bereich:</strong>
                  <span>{display?.wärmepumpeBereiche ?? "-"}</span>
                </div>
              )}
            </div>
          </section>

          <section style={summarySectionStyle}>
            <h3 className="h3">Treibstoff</h3>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid #e5e7eb" }}>
                  <th style={{ textAlign: "left", padding: "4px 8px" }}>Typ</th>
                  <th style={{ textAlign: "left", padding: "4px 8px" }}>Einheit</th>
                  <th style={{ textAlign: "left", padding: "4px 8px" }}>Menge</th>
                </tr>
              </thead>
              <tbody>
                {(display.treibstoff ?? []).map((r, i) => (
                  <tr key={i} style={{ borderBottom: "1px solid #f1f5f9" }}>
                    <td style={{ padding: "4px 8px" }}>{r.type || "-"}</td>
                    <td style={{ padding: "4px 8px" }}>{r.unit || "-"}</td>
                    <td style={{ padding: "4px 8px" }}>{r.amount || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          <section style={summarySectionStyle}>
            <h3 className="h3">Lackierkabinen</h3>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid #e5e7eb" }}>
                  <th style={{ textAlign: "left", padding: "4px 8px" }}>Art</th>
                </tr>
              </thead>
              <tbody>
                {(display.lackierkabinen ?? []).map((r, i) => (
                  <tr key={i} style={{ borderBottom: "1px solid #f1f5f9" }}>
                    <td style={{ padding: "4px 8px" }}>{r.area || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          <section style={summarySectionStyle}>
            <h3 className="h3">Fossile Fahrzeuge</h3>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid #e5e7eb" }}>
                  <th style={{ textAlign: "left", padding: "4px 8px" }}>Treibstofftyp</th>
                  <th style={{ textAlign: "left", padding: "4px 8px" }}>Anzahl</th>
                </tr>
              </thead>
              <tbody>
                {(display.fossileFahrzeuge ?? []).map((r, i) => (
                  <tr key={i} style={{ borderBottom: "1px solid #f1f5f9" }}>
                    <td style={{ padding: "4px 8px" }}>{r.type || "-"}</td>
                    <td style={{ padding: "4px 8px" }}>{r.amount || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          <section style={summarySectionStyle}>
            <h3 className="h3">Beleuchtung</h3>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid #e5e7eb" }}>
                  <th style={{ textAlign: "left", padding: "4px 8px" }}>Typ</th>
                  <th style={{ textAlign: "left", padding: "4px 8px" }}>Anzahl</th>
                </tr>
              </thead>
              <tbody>
                {(display.beleuchtung ?? []).map((r, i) => (
                  <tr key={i} style={{ borderBottom: "1px solid #f1f5f9" }}>
                    <td style={{ padding: "4px 8px" }}>{r.type || "-"}</td>
                    <td style={{ padding: "4px 8px" }}>{r.amount || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          <section style={summarySectionStyle}>
            <h3 className="h3">Verbrauchsdaten</h3>
            <ul>
              <li>
                Strom: <b>{fmtUnit(display.verbrauch?.strom_kwh_m2, "kWh/Jahr")}</b>
              </li>
              <li>
                Heizung: <b>{fmtUnit(display.verbrauch?.heizung_kwh_m2, "kWh/Jahr")}</b>
              </li>
              <li>
                Abfall (t/Jahr): <b>{display?.abfall?.menge_tonnen ?? "-"}</b>
              </li>
              <li>
                Lack (CHF/Jahr): <b>{display?.verbrauchsmaterialien?.lack_chf ?? "-"}</b>
              </li>
              <li>
                Verbrauchsmaterialien (CHF/Jahr):{" "}
                <b>{display?.verbrauchsmaterialien?.materialien_chf ?? "-"}</b>
              </li>
              <li>
                Ersatzteile (CHF/Jahr): <b>{display?.verbrauchsmaterialien?.ersatzteile_chf ?? "-"}</b>
              </li>
            </ul>
          </section>

          <div style={{ display: "flex", gap: 12, marginTop: "1.5rem" }}>
            <sdx-button theme="secondary" label="Bearbeiten" onClick={() => { setStep("form"); window.scrollTo(0, 0); }} />
            <sdx-button label="Speichern & Upload" onClick={uploadCSVToS3} />
          </div>
        </div>
      )}
    </div>
  );
}

function KpiCard({ title, value }) {
  return (
    <div
      style={{
        border: "1px solid #e5e7eb",
        borderRadius: 12,
        padding: 16,
        background: "#fff",
      }}
    >
      <div style={{ fontSize: 12, color: "#64748b" }}>{title}</div>
      <div style={{ fontSize: 24, fontWeight: 600, marginTop: 4 }}>{value}</div>
    </div>
  );
}
